import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check, Plus, ArrowLeftRight, Info, Trash2, Settings2, Link2, MoreVertical,
  Flame, Weight, TrendingUp, TrendingDown, MoveVertical, CheckCheck, StickyNote,
} from 'lucide-react';

import { useWorkoutMeta } from '../../contexts/WorkoutMetaContext';
import { useWorkoutData } from '../../contexts/WorkoutDataContext';
import { useWorkoutProgress } from '../../contexts/WorkoutProgressContext';
import { useLanguage } from '../../contexts/LanguageContext';

import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

import TechniqueCueBubbleInline from '../TechniqueCueBubbleInline';
import { exerciseName, getExerciseInputMode, getCardioFields, isTimeBasedExercise } from '../../lib/exerciseDisplay';
import { getExerciseKey } from '../../utils/exerciseKey';
import { getMatchingPreviousSet, getSetDisplayLabel } from './liveSessionUtils';

import SetRow from './SetRow';
import { ExerciseThumb } from './LiveSession';
import { cardioGridClass } from './cardioGridLayouts';

/* ════════════════════════════════════════════════════════════════
   <ExerciseRow>

   Renders one exercise card: header (image, name, swap, options
   menu), technique-cue bubble, session-note input, progression
   suggestion pill, column headers, the .map of <SetRow> children,
   and the "add set" button at the bottom.

   Reads workoutData / lastWeights / appliedProgression from
   context. Receives LiveSession-local handlers (completeSet,
   updateSetData, etc.) via the `liveActions` prop. Those
   handlers are wrapped through a `ref` so that the callbacks we
   pass to SetRow stay reference-stable even when liveActions
   itself changes every render — that's what keeps SetRow's memo
   intact across keystrokes.
   ════════════════════════════════════════════════════════════════ */
export default function ExerciseRow({
  exIdx,
  exercise,
  isLast,
  supersetGroupId,
  supersetLabel,
  setContextMenuState,
  exerciseContextMenuState,
  cuesMap,
  exerciseImages,
  sessionNoteValue,
  onSessionNoteChange,
  progressionStep,
  onProgressionStepChange,
  liveActions,
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { workoutPrefs, workoutSessionKey } = useWorkoutMeta();
  const { workoutData, lastWeights, appliedProgression } = useWorkoutData();
  const { completedSets, incompleteSets } = useWorkoutProgress();

  // ── Stabilize LiveSession-bound callbacks via ref pattern ─────
  // liveActions is a fresh object each render in LiveSession; the
  // callbacks inside close over local LiveSession state. Reading them
  // through a ref lets us expose useCallback-stabilized wrappers (deps
  // = []) that keep referential equality across keystrokes — without
  // which SetRow's memo would not isolate.
  const liveActionsRef = useRef(liveActions);
  useEffect(() => { liveActionsRef.current = liveActions; });

  const handleUpdateSet = useCallback((eIdx, sIdx, field, value) => {
    liveActionsRef.current.updateSetData(eIdx, sIdx, field, value);
  }, []);
  const handleCompleteSet = useCallback((eIdx, sIdx) => {
    liveActionsRef.current.completeSet(eIdx, sIdx);
  }, []);
  const handleUncompleteSet = useCallback((eIdx, sIdx) => {
    liveActionsRef.current.uncompleteSet(eIdx, sIdx);
  }, []);
  const handleDeleteSet = useCallback((eIdx, sIdx) => {
    liveActionsRef.current.deleteSet(eIdx, sIdx);
  }, []);
  const handleLongPressStart = useCallback((event, eIdx, sIdx) => {
    liveActionsRef.current.beginSetLongPress(event, eIdx, sIdx);
  }, []);
  const handleLongPressMove = useCallback((event) => {
    liveActionsRef.current.moveSetLongPress(event);
  }, []);
  const handleLongPressEnd = useCallback(() => {
    liveActionsRef.current.endSetLongPress();
  }, []);
  const handleSetContextMenuOpen = useCallback((eIdx, sIdx) => {
    liveActionsRef.current.openSetContextMenu(eIdx, sIdx);
  }, []);
  const handleSetContextMenuClose = useCallback(() => {
    liveActionsRef.current.closeSetContextMenu();
  }, []);

  // Bundled action object — useMemo with [] keeps the same ref forever.
  // Each member calls through liveActionsRef so the closures see latest
  // LiveSession state without breaking memo.
  const setActions = useMemo(() => ({
    onChangeSetType: (eIdx, sIdx, type) => liveActionsRef.current.changeSetType(eIdx, sIdx, type),
    onUpdateSetDuration: (eIdx, sIdx, part, value) => liveActionsRef.current.updateSetDuration(eIdx, sIdx, part, value),
    onUpdateSetCardioField: (eIdx, sIdx, key, value) => liveActionsRef.current.updateSetCardioField(eIdx, sIdx, key, value),
    onUpdateSetRPE: (eIdx, sIdx, value) => liveActionsRef.current.updateSetRPE(eIdx, sIdx, value),
    onDuplicateSet: (eIdx, sIdx) => liveActionsRef.current.duplicateSet(eIdx, sIdx),
    onAddSetBelow: (eIdx, sIdx) => liveActionsRef.current.addSetBelow(eIdx, sIdx),
    onSetCompletedToggle: (eIdx, sIdx, isDone) => liveActionsRef.current.toggleSetCompleted(eIdx, sIdx, isDone),
  }), []);

  // ── Derived row data ──────────────────────────────────────────
  // Three input modes: 'reps' (kg+reps), 'time' (mm:ss), 'cardio' (min + data-driven metrics).
  // isTimeBased keeps non-reps gating semantics for the legacy progression-pill check.
  const inputMode = getExerciseInputMode(exercise);
  const cardioFields = useMemo(
    () => (inputMode === 'cardio' ? getCardioFields(exercise) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only name/equipment affect the profile; `exercise` identity churns every render
    [inputMode, exercise.name, exercise.equipment],
  );
  const isTimeBased = isTimeBasedExercise(exercise);
  const lastWeight = lastWeights[exercise.name];
  const allDone = exercise.sets.every((_, si) => completedSets[`${exIdx}-${si}`]);
  const appliedDirection = appliedProgression.get(`${workoutSessionKey}-${exIdx}`) || null;
  const hasAppliedProgression = appliedProgression.has(`${workoutSessionKey}-${exIdx}`);

  return (
    <div
      key={exercise.live_id || `${exercise.exercise_id}-${exIdx}`}
      data-testid={`exercise-block-${exIdx}`}
      className={supersetGroupId !== undefined ? `border-l-4 ${liveActions.getSupersetColor(supersetGroupId)} pl-3 rounded-l-lg` : ''}
    >
      {/* Superset label */}
      {supersetLabel && (
        <div className="flex items-center gap-2 mb-2">
          <div className={`px-2 py-0.5 rounded-full text-xs font-bold text-white ${liveActions.getSupersetBgColor(supersetGroupId)}`}>
            {supersetLabel}
          </div>
        </div>
      )}
      {/* Exercise header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          {/* Exercise image — tap to open demo */}
          <div
            onClick={() => liveActions.openExerciseDetail(exercise.name, exIdx)}
            className="relative w-10 h-10 rounded-xl overflow-hidden bg-neutral-800 flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-primary hover:ring-offset-1 transition-all group/thumb"
            data-testid={`exercise-info-btn-${exIdx}`}
            title={t('workout.viewExerciseDemo')}
          >
            <ExerciseThumb name={exercise.name} gifUrl={exercise.gifUrl || exercise.image_url} exerciseImages={exerciseImages} />
            {allDone && (
              <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center">
                <Check className="h-4 w-4 text-white" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <div
                onClick={(event) => {
                  if (liveActions.consumeReorderLongPressTriggered()) {
                    event.preventDefault();
                    return;
                  }
                  liveActions.openExerciseDetail(exercise.name, exIdx);
                }}
                onPointerDown={(e) => liveActions.beginReorderLongPress(e, exIdx)}
                onPointerUp={liveActions.endReorderLongPress}
                onPointerLeave={liveActions.endReorderLongPress}
                onPointerCancel={liveActions.endReorderLongPress}
                onPointerMove={liveActions.moveReorderLongPress}
                onContextMenu={(event) => {
                  event.preventDefault();
                  liveActions.openExerciseContextMenu(exIdx);
                }}
                className="cursor-pointer hover:opacity-80 transition-opacity min-w-0 select-none"
                data-testid={`exercise-name-btn-${exIdx}`}
                title={t('workout.tapDemoHold')}
                style={{ WebkitTouchCallout: 'none' }}
              >
                <h3 className="text-base font-bold leading-tight truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {(exerciseName(exercise, language) || '').toUpperCase()}
                </h3>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => liveActions.openSwapModal(exIdx)}
                    className="flex-shrink-0 w-10 h-10 md:w-7 md:h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/90 transition-colors"
                    data-testid={`swap-exercise-btn-${exIdx}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="md:w-3 md:h-3">
                      <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                      <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                    </svg>
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('tooltips.swapExercise')}</TooltipContent>
              </Tooltip>
            </div>
            <p className="text-xs text-muted-foreground capitalize">{(exercise.targetMuscles || []).join(', ') || exercise.muscle_group}{exercise.is_adhoc ? ` · ${t('workout.adhocLabel')}` : ''} · {t('workout.tapForDemo')}</p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 md:gap-1 flex-shrink-0">
          <Popover
            open={exerciseContextMenuState.open && exerciseContextMenuState.exIdx === exIdx}
            onOpenChange={(open) => liveActions.setExerciseContextMenuOpen(open, exIdx)}
          >
            <PopoverTrigger asChild>
              <button
                className="w-10 h-10 md:w-8 md:h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                data-testid={`exercise-options-btn-${exIdx}`} title="Exercise options">
                <MoreVertical className="h-4 w-4 md:h-3.5 md:w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-1.5" align="end" side="bottom">
              <p className="px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide truncate text-muted-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {exerciseName(exercise, language)}
              </p>
              {(() => {
                const close = () => liveActions.closeExerciseContextMenu();
                const menuItem = (icon, label, color, onClick) => (
                  <button key={label} onClick={() => { close(); onClick(); }}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-muted/60 transition-colors text-left ${color || ''}`}>
                    <span className="flex-shrink-0">{icon}</span>
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                );
                return (
                  <div className="space-y-0.5 mt-1">
                    {menuItem(<MoveVertical className="h-4 w-4 text-primary" />, t('workout.ctxReorder'), '', () => liveActions.enterReorderMode())}
                    {menuItem(<ArrowLeftRight className="h-4 w-4 text-blue-400" />, t('workout.ctxExchange'), '', () => liveActions.openSwapModal(exIdx))}
                    {menuItem(<Info className="h-4 w-4 text-purple-400" />, t('workout.ctxViewInfo'), '', () => liveActions.openExerciseDetail(workoutData[exIdx]?.name, exIdx))}
                    {menuItem(<CheckCheck className="h-4 w-4 text-green-400" />, t('workout.ctxMarkAllDone'), '', () => liveActions.markAllSetsDone(exIdx))}
                    {menuItem(<Plus className="h-4 w-4 text-cyan-400" />, t('workout.ctxAddSet'), '', () => liveActions.addSetToExercise(exIdx))}
                    {menuItem(<StickyNote className="h-4 w-4 text-yellow-400" />, t('workout.ctxAddNote'), '', () => liveActions.focusExerciseNote(exIdx))}
                    {(() => {
                      const firstWeighted = (workoutData[exIdx]?.sets || []).find(s => (s.weight || 0) > 0);
                      if (!firstWeighted) return null;
                      return menuItem(
                        <Weight className="h-4 w-4 text-orange-400" />,
                        t('workout.ctxPlateCalc'),
                        '',
                        () => liveActions.openPlateCalc(firstWeighted.weight, exerciseName(exercise, language)),
                      );
                    })()}
                    {(() => {
                      const sets = workoutData[exIdx]?.sets || [];
                      const firstWork =
                        sets.find(s => (s.set_type || 'work') === 'work' && (s.weight || 0) > 0)
                        || sets.find(s => (s.weight || 0) > 0)
                        || sets[0]
                        || {};
                      return menuItem(
                        <Flame className="h-4 w-4 text-orange-400" />,
                        t('workout.ctxWarmupCalc', 'Warm-up calculator'),
                        '',
                        () => liveActions.openWarmupCalc({
                          weight: firstWork.weight || 0,
                          reps: firstWork.target_reps_max || firstWork.target_reps_min || firstWork.completed_reps || 5,
                          exercise: {
                            name: exerciseName(exercise, language),
                            equipment: exercise.equipment || '',
                          },
                        }),
                      );
                    })()}
                    {exIdx < workoutData.length - 1 && menuItem(
                      <Link2 className={`h-4 w-4 ${supersetGroupId !== undefined ? 'text-cyan-400' : 'text-muted-foreground'}`} />,
                      supersetGroupId !== undefined ? t('workout.removeSuperset') : t('workout.linkSuperset'),
                      supersetGroupId !== undefined ? 'text-cyan-400' : '',
                      () => liveActions.toggleSuperset(exIdx),
                    )}
                    {/* Step size config — always available */}
                    <div className="px-2.5 py-1.5">
                      <div className="flex items-center gap-2">
                        <Settings2 className="h-4 w-4 text-amber-400 flex-shrink-0" />
                        <span className="text-sm font-medium flex-1">{t('workout.ctxStepSize')}</span>
                        <Input type="number" step="0.5" min="0.5"
                          value={progressionStep}
                          onChange={(e) => onProgressionStepChange(exercise.name, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-7 w-16 text-center text-xs"
                          data-testid={`progression-step-input-${exIdx}`} />
                        <span className="text-xs text-muted-foreground">kg</span>
                      </div>
                    </div>
                    <div className="h-px bg-border/50 my-0.5" />
                    {menuItem(<Trash2 className="h-4 w-4 text-red-400" />, t('workout.ctxDelete'), 'text-red-400', () => liveActions.openRemoveExerciseConfirm(exIdx))}
                  </div>
                );
              })()}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Technique cue bubble — coach tip for this exercise */}
      <div className="mb-2 ml-[52px]">
        <TechniqueCueBubbleInline cue={cuesMap[getExerciseKey(exercise)]} />
      </div>

      {/* Session notes input (always visible) */}
      <div className="mb-2 ml-[52px]">
        <input
          value={sessionNoteValue}
          onChange={(e) => onSessionNoteChange(exIdx, e.target.value)}
          placeholder={t('workout.sessionNotePlaceholder')}
          className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-border/50 bg-muted/30 focus:outline-none focus:ring-1 focus:ring-primary/40 placeholder:text-muted-foreground/50"
          data-testid={`session-note-input-${exIdx}`}
        />
      </div>
      {/* Routine note hint (from routine, read-only) */}
      {exercise.notes && !exercise.notes.startsWith('Primary:') && (
        <p className="text-xs text-muted-foreground/70 italic mb-2 ml-[68px] truncate" title={exercise.notes}>
          {exercise.notes}
        </p>
      )}

      {/* Progression suggestion pill — exercise-level action, lives above the sets so it doesn't steal KG column width */}
      {!isTimeBased && workoutPrefs.showProgressionBadges && !hasAppliedProgression && (lastWeight?.suggested_increase > 0 || lastWeight?.suggested_decrease > 0) && (
        <div className="mb-2 ml-[52px] flex items-center gap-2">
          {lastWeight?.suggested_increase > 0 && (
            <button
              onClick={() => liveActions.applyProgression(exIdx, 'increase')}
              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-green-500 hover:bg-green-600 active:scale-95 transition-all shadow-sm"
              data-testid={`progression-badge-${exIdx}`}
              title={t('tooltips.increaseWeight')}
            >
              <TrendingUp className="h-3.5 w-3.5 text-white" />
              <span className="text-xs font-semibold text-white">+{progressionStep}kg</span>
            </button>
          )}
          {lastWeight?.suggested_decrease > 0 && (
            <button
              onClick={() => liveActions.applyProgression(exIdx, 'decrease')}
              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 transition-all shadow-sm"
              data-testid={`deload-badge-${exIdx}`}
              title={t('tooltips.decreaseWeight')}
            >
              <TrendingDown className="h-3.5 w-3.5 text-white" />
              <span className="text-xs font-semibold text-white">-{progressionStep}kg</span>
            </button>
          )}
        </div>
      )}

      {/* Column headers — three layouts:
            cardio : Set | Prev | MIN     | …metrics…       | (RPE) | (✓)
            time   : Set | Prev | TIME              | (RPE) | (✓)
            reps   : Set | Prev | KG      | REPS    | (RPE) | (✓) */}
      {inputMode === 'cardio' ? (
        <div className={`grid ${cardioGridClass(cardioFields.length, workoutPrefs.rpeTrackingEnabled)} gap-2 px-1 mb-1.5`}>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colSet')}</span>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colPrevious')}</span>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colMinutes')}</span>
          {cardioFields.map((f) => (
            <span key={f.key} className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t(f.headerKey)}</span>
          ))}
          {workoutPrefs.rpeTrackingEnabled && <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colRPE')}</span>}
          <span></span>
        </div>
      ) : inputMode === 'time' ? (
        <div className={`grid ${workoutPrefs.rpeTrackingEnabled ? 'grid-cols-[44px_60px_1fr_48px_36px]' : 'grid-cols-[44px_60px_1fr_36px]'} gap-2 px-1 mb-1.5`}>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colSet')}</span>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colPrevious')}</span>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colTime')}</span>
          {workoutPrefs.rpeTrackingEnabled && <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colRPE')}</span>}
          <span></span>
        </div>
      ) : (
        <div className={`grid ${workoutPrefs.rpeTrackingEnabled ? 'grid-cols-[44px_60px_1fr_1fr_48px_36px]' : 'grid-cols-[44px_60px_1fr_1fr_36px]'} gap-2 px-1 mb-1.5`}>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colSet')}</span>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colPrevious')}</span>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colKG')}</span>
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colReps')}</span>
          {workoutPrefs.rpeTrackingEnabled && <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colRPE')}</span>}
          <span></span>
        </div>
      )}

      {/* Sets list — each row is a memoized SetRow */}
      <div className="space-y-1.5">
        {exercise.sets.map((set, setIdx) => {
          const compoundKey = `${exIdx}-${setIdx}`;
          const isCompleted = !!completedSets[compoundKey];
          const isIncomplete = incompleteSets.has(compoundKey);
          const previousSet = getMatchingPreviousSet(lastWeight?.weights, exercise.sets, setIdx);
          const setLabel = getSetDisplayLabel(exercise.sets, setIdx);
          const isContextMenuOpen = setContextMenuState.open
            && setContextMenuState.exIdx === exIdx
            && setContextMenuState.setIdx === setIdx;

          return (
            <SetRow
              key={set._key || `${exIdx}-${setIdx}-${set.set_number}`}
              exIdx={exIdx}
              setIdx={setIdx}
              set={set}
              isCompleted={isCompleted}
              isIncomplete={isIncomplete}
              previousSet={previousSet}
              exerciseEquipment={exercise.equipment}
              appliedDirection={appliedDirection}
              prefs={workoutPrefs}
              inputMode={inputMode}
              cardioFields={cardioFields}
              setLabel={setLabel}
              isContextMenuOpen={isContextMenuOpen}
              onUpdateSet={handleUpdateSet}
              onCompleteSet={handleCompleteSet}
              onUncompleteSet={handleUncompleteSet}
              onDeleteSet={handleDeleteSet}
              onLongPressStart={handleLongPressStart}
              onLongPressMove={handleLongPressMove}
              onLongPressEnd={handleLongPressEnd}
              onContextMenuOpen={handleSetContextMenuOpen}
              onContextMenuClose={handleSetContextMenuClose}
              setActions={setActions}
            />
          );
        })}
      </div>

      {/* Add set */}
      <button
        onClick={() => liveActions.addSetToExercise(exIdx)}
        className="mt-2 w-full py-2 text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 rounded-lg border border-dashed border-border hover:border-foreground/30 transition-colors"
        data-testid={`add-set-btn-${exIdx}`}
      >
        <Plus className="h-3 w-3" /> {t('workout.addSet')}
      </button>

      {/* Divider between exercises (but not between superset pairs) */}
      {!isLast && <div className="mt-6 border-t border-border/40" />}
    </div>
  );
}
